import L from "leaflet";
import { getGlMap } from "./leafletMaplibreInternals";

/**
 * 地図出力前のレイヤー描画完了待ちユーティリティ。
 *
 * 元 useMapImageExport.ts には旧 waitForLayersReady (1000ms ポーリング、
 * レイヤーごとに setInterval、ゼンリン ImageOverlay のみ個別待ち) と
 * 新 waitForMapRender (150ms ポーリング、leaflet-tile img の complete
 * チェック) の 2 系統が存在していた。本関数は両者の長所を統合し、
 *  - leaflet-tile img の complete && naturalWidth > 0
 *  - leaflet-image-layer img (ゼンリン bluemap 等の ImageOverlay) の complete
 *  - 各レイヤーの内部 _loading フラグ
 *  - maplibre レイヤーの loaded()
 * をすべて満たすか、タイムアウトするまで待つ。
 *
 * 副作用なし（ただし開始時に GridLayer.redraw / glMap.triggerRepaint を呼ぶ）。
 * タイムアウト時の UI 通知は呼び出し側に委ねる (onTimeout コールバック)。
 */

interface LoadingHolder {
  _loading?: boolean;
}

const getInternalLoadingValue = (
  layer: L.Layer | undefined | null
): boolean | null | undefined => {
  if (!layer) return null;
  return (layer as LoadingHolder)._loading;
};

const isImageLoaded = (img: HTMLImageElement): boolean => {
  // display:none の要素は読み込み待ちの対象外。
  if (img.style.display === "none") return true;
  return img.complete && img.naturalWidth > 0;
};

export interface WaitForMapReadyProgress {
  totalTiles: number;
  loadedTiles: number;
  loadingOverlay: boolean;
  loadingLayer: boolean;
}

export interface WaitForMapReadyOptions {
  /** ポーリング間隔 (ms)。デフォルト 150ms。 */
  pollingIntervalMs?: number;
  /** 最大待機時間 (ms)。デフォルト 10000ms。 */
  maxTimeoutMs?: number;
  /**
   * 最大待機時間を超過したときに呼ばれるコールバック。
   * トースト等の UI 通知は呼び出し側で行う。
   */
  onTimeout?: () => void;
  /** 進捗状況を通知するコールバック */
  onProgress?: (progress: WaitForMapReadyProgress) => void;
}

export const checkMapLoadingProgress = (
  mapInstance: L.Map
): WaitForMapReadyProgress => {
  const container = mapInstance.getContainer();

  // タイル画像 (img.leaflet-tile) の読み込み完了チェック。
  const tileImages = Array.from(
    container.querySelectorAll<HTMLImageElement>("img.leaflet-tile")
  );
  const totalTiles = tileImages.length;
  const loadedTiles = tileImages.filter((img) => isImageLoaded(img)).length;

  // ImageOverlay 系 (ゼンリン bluemap 等) の読み込み完了チェック。
  const overlayImages = Array.from(
    container.querySelectorAll<HTMLImageElement>("img.leaflet-image-layer")
  );
  const loadingOverlayExists = overlayImages.some((img) => !isImageLoaded(img));

  // 各レイヤーの内部状態をチェック。
  let loadingLayerExists = false;
  mapInstance.eachLayer((layer) => {
    if (getInternalLoadingValue(layer)) {
      loadingLayerExists = true;
    }
    const glMap = getGlMap(layer);
    if (glMap && !glMap.loaded()) {
      loadingLayerExists = true;
    }
  });

  return {
    totalTiles,
    loadedTiles,
    loadingOverlay: loadingOverlayExists,
    loadingLayer: loadingLayerExists,
  };
};

/**
 * mapInstance 上のレイヤー描画完了を待つ。
 *
 * タイムアウト時は onTimeout を呼んで resolve する（reject しない）。
 * 出力処理を続行する判断は呼び出し側に委ねる設計。
 */
export const waitForMapReady = async (
  mapInstance: L.Map,
  options: WaitForMapReadyOptions = {}
): Promise<void> => {
  const pollingIntervalMs = options.pollingIntervalMs ?? 150;
  const maxTimeoutMs = options.maxTimeoutMs ?? 10_000;

  await new Promise<void>((resolve) => {
    mapInstance.whenReady(resolve);
  });

  // 出力前に各レイヤーへ再描画を依頼する。
  // GridLayer は redraw、maplibre レイヤーは triggerRepaint で描画キックを行う。
  mapInstance.eachLayer((layer) => {
    if (layer instanceof L.GridLayer) {
      layer.redraw();
    }
    const glMap = getGlMap(layer);
    if (glMap) {
      glMap.triggerRepaint();
    }
  });

  await new Promise<void>((resolve) => {
    const startedAt = Date.now();

    // 初回進捗を0に設定
    const initialProgress: WaitForMapReadyProgress = {
      totalTiles: 0,
      loadedTiles: 0,
      loadingOverlay: false,
      loadingLayer: false,
    };
    options.onProgress?.(initialProgress);
    let loopCount = 0;

    const timer = window.setInterval(() => {
      // 進捗確認の処理を関数に切り出しました
      const progress = checkMapLoadingProgress(mapInstance);
      // 最初の値が安定しないので6ループ以上経過しないとプログレスを表示しない
      if (loopCount++ >= 6) {
        options.onProgress?.(progress);
      }

      const ready =
        progress.loadedTiles >= progress.totalTiles &&
        !progress.loadingOverlay &&
        !progress.loadingLayer;
      const timedOut = Date.now() - startedAt > maxTimeoutMs;

      if (ready || timedOut) {
        window.clearInterval(timer);
        if (timedOut && !ready) {
          options.onTimeout?.();
        }
        resolve();
      }
    }, pollingIntervalMs);
  });
};
